import type { EditorCanvas } from "replo-utils/lib/misc/canvas";
import type { MediaSize } from "schemas/breakpoints";

import * as React from "react";

import {
  MEDIA_MAX_SIZE,
  mediaQueries,
} from "replo-runtime/shared/utils/breakpoints";
import { mediaSizes } from "schemas/breakpoints";

import {
  EditorCanvasContext,
  GlobalWindowContext,
  RenderEnvironmentContext,
  RuntimeHooksContext,
  useRuntimeContext,
} from "../../shared/runtime-context";

const editorCanvasToMediaSize: Record<EditorCanvas, MediaSize> = {
  mobile: "sm",
  tablet: "md",
  desktop: "lg",
};

/**
 * This function returns the current real mediaSize and auto updates it when
 * resize happens.
 */
export function useCurrentMediaSize() {
  const globalWindow = useRuntimeContext(GlobalWindowContext);
  const isEditorCanvas =
    useRuntimeContext(
      RuntimeHooksContext,
    ).useIsEditorEditModeRenderEnvironment();
  const editorCanvas = useRuntimeContext(EditorCanvasContext);
  const { isEditorApp } = useRuntimeContext(RenderEnvironmentContext);
  const isPreviewMode = isEditorApp && !isEditorCanvas;
  const previewWidth = useRuntimeContext(RuntimeHooksContext).usePreviewWidth();
  let editorMediaSize = editorCanvasToMediaSize[editorCanvas];
  if (isPreviewMode) {
    if (previewWidth > MEDIA_MAX_SIZE.md) {
      editorMediaSize = "lg";
    } else if (previewWidth > MEDIA_MAX_SIZE.sm) {
      editorMediaSize = "md";
    } else {
      editorMediaSize = "sm";
    }
  }

  const [mediaSize, setMediaSize] = React.useState<MediaSize>();

  React.useEffect(() => {
    if (!globalWindow || editorMediaSize) {
      return;
    }
    const cleanupFunctions: Array<() => void> = [];
    let initialMatchFound = false;
    for (const size of mediaSizes) {
      const queryString = mediaQueries[size];
      const queryList = globalWindow.matchMedia(queryString);

      // Note (Evan, 2024-07-30): Apparently in some cases, matchMedia
      // can return null. If this happens, just skip this mediaSize, since
      // we can't add an event listener.
      if (!queryList) {
        continue;
      }

      const handleChange = (event: MediaQueryListEvent) => {
        if (event.matches) {
          setMediaSize(size);
        }
      };
      queryList.addEventListener("change", handleChange);
      cleanupFunctions.push(() => {
        return queryList.removeEventListener("change", handleChange);
      });

      if (!initialMatchFound && queryList.matches) {
        setMediaSize(size);
        initialMatchFound = true;
      }
    }

    return () => {
      for (const cleanup of cleanupFunctions) {
        cleanup();
      }
    };
  }, [globalWindow, editorMediaSize]);
  return editorMediaSize ?? mediaSize;
}
